import { useEffect, useState } from 'react';
import { BRAND } from '@shared/model';
import { Brand, CustomerMark } from './components/Brand';
import { TopNav } from './components/TopNav';
import { Admin } from './screens/Admin';
import { Dashboard } from './screens/Dashboard';
import { FarmMap } from './screens/FarmMap';
import { Farmers } from './screens/Farmers';
import { Block } from './screens/Block';
import { Site3D } from './screens/Site3D';
import { SignIn } from './screens/SignIn';
import { RosterProvider } from './state/RosterContext';
import { SessionProvider, useSession } from './state/SessionContext';

function useHashRoute(): string {
  const [hash, setHash] = useState(window.location.hash || '#/');
  useEffect(() => {
    const on = (): void => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return hash;
}

/** "#/map/h1234?x=1" -> { path: "/map/h1234", query } */
function parse(hash: string): { path: string; parts: string[]; query: URLSearchParams } {
  const raw = hash.replace(/^#/, '') || '/';
  const [path, qs] = raw.split('?');
  const parts = (path ?? '/').split('/').filter(Boolean);
  return { path: path ?? '/', parts, query: new URLSearchParams(qs ?? '') };
}

/** Site 3D for anyone with the link: public imagery and audited-site files only, no farmer data. */
function OpenSite3D({ query }: { query: URLSearchParams }): JSX.Element {
  return (
    <div className="shell shell--fit">
      <header className="nav">
        <Brand href="#/site3d" />
        <span className="nav__sep" />
        <CustomerMark />
        <div className="nav__end">
          <a className="btn-ghost" href="#/">Staff sign in</a>
        </div>
      </header>
      <main className="shell__main"><Site3D query={query} /></main>
    </div>
  );
}

function Gate(): JSX.Element {
  const { account } = useSession();
  const route = useHashRoute();
  const { parts, query } = parse(route);
  const screen = parts[0] ?? '';
  if (account === undefined) return <div className="gate-shell"><div className="gate-body"><p className="muted">Checking who you are…</p></div></div>;
  if (!account) return screen === 'site3d' ? <OpenSite3D query={query} /> : <SignIn />;
  return (
    <RosterProvider>
      <div className={`shell${screen === 'site3d' ? ' shell--fit' : ''}`}>
        <TopNav route={route} />
        <main className="shell__main">
          {screen === 'map' ? <FarmMap holderId={parts[1] ?? null} />
            : screen === 'block' ? <Block rank={parts[1] ? Number(parts[1]) || null : null} />
            : screen === 'site3d' ? <Site3D query={query} />
            : screen === 'farmers' ? <Farmers query={query} />
            : screen === 'farmer' && parts[1] ? <Farmers query={new URLSearchParams({ holder: parts[1] })} />
            : screen === 'admin' ? <Admin />
            : <Dashboard />}
        </main>
        {screen !== 'map' && screen !== 'block' && screen !== 'site3d' ? (
          <footer className="footer">
            <span>{BRAND.product} for <strong>{BRAND.customer}</strong>, {BRAND.customerGroup}</span>
            <span className="footer__sep" />
            <span>powered by {BRAND.poweredBy}</span>
            <span className="footer__spacer" />
            <span className="footer__copy">© {new Date().getFullYear()} {BRAND.owner}. All rights reserved. · {BRAND.contractRef}</span>
          </footer>
        ) : null}
      </div>
    </RosterProvider>
  );
}

export function App(): JSX.Element {
  return (
    <SessionProvider>
      <Gate />
    </SessionProvider>
  );
}
